import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MediaPickerDialogComponent } from '../../media-picker-dialog/media-picker-dialog.component';
import { OperatorAdminService } from '../../../services/operator-admin/operator-admin.service';
import { ToastService } from '../../../services/toast/toast.service';
import { LoaderComponent } from '../../../atoms/loader/loader.component';

// add / edit a central utsav instance (image picked from the media library). data.utsav =
// row when editing (null when adding). self-saving. New utsav saves INACTIVE;
// a separate Activate action flips it to ACTIVE.
@Component({
  selector: 'app-oa-utsav-form-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, LoaderComponent],
  templateUrl: './oa-utsav-form-dialog.component.html',
  styleUrls: ['./oa-utsav-form-dialog.component.scss', '../../../../common-style/ac-dialog.scss'],
})
export class OaUtsavFormDialogComponent implements OnInit {
  form: FormGroup;
  isEdit: boolean = false;
  isLoading: boolean = false;
  // the active festivals (utsav masters) for the parent dropdown (add mode)
  masters: any[] = [];

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<OaUtsavFormDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { utsav: any | null },
    private operatorAdminService: OperatorAdminService,
    private toastService: ToastService,
    private dialog: MatDialog
  ) {
    const isEdit = !!this.data?.utsav;
    this.form = this.fb.group({
      // adding picks the parent festival by id; editing never moves an instance
      utsavId: ['', isEdit ? [] : Validators.required],
      utsavCode: [''],
      utsavTitle: ['', Validators.required],
      // the server requires all of these — mirror it so the dialog says so upfront
      utsavAbbrev: ['', Validators.required],
      utsavYear: ['', Validators.required],
      utsavLocation: ['', Validators.required],
      celebrationMode: [''],
      utsavStartDate: ['', Validators.required],
      utsavEndDate: ['', Validators.required],
      genReqStartDate: ['', Validators.required],
      genReqEndDate: ['', Validators.required],
      workerReqStartDate: [''],
      workerReqEndDate: [''],
      accommodationStartDate: ['', Validators.required],
      accommodationRosterDate: ['', Validators.required],
      isExternalHotelInvolved: [false],
      isPandalInvolved: [false],
      statusReason: [''],
      comments: [''],
      photoUrl: [''],
    });
  }

  ngOnInit(): void {
    const u = this.data?.utsav;
    if (!u) {
      this.operatorAdminService.listUtsavMasters().subscribe({
        next: (res: any) => (this.masters = res?.data?.masters || []),
        error: (err) => this.toastService.error(err?.error?.message || 'Failed to load utsavs.'),
      });
    }
    if (u) {
      this.isEdit = true;
      this.form.patchValue({
        utsavCode: u.utsavCode || '',
        utsavTitle: u.title || '',
        utsavAbbrev: u.abbrev || '',
        utsavYear: u.year || '',
        utsavLocation: u.location || '',
        celebrationMode: u.celebrationMode || '',
        utsavStartDate: u.startDate || '',
        utsavEndDate: u.endDate || '',
        genReqStartDate: u.genReqStartDate || '',
        genReqEndDate: u.genReqEndDate || '',
        workerReqStartDate: u.workerReqStartDate || '',
        workerReqEndDate: u.workerReqEndDate || '',
        accommodationStartDate: u.accommodationStartDate || '',
        accommodationRosterDate: u.accommodationRosterDate || '',
        isExternalHotelInvolved: u.isExternalHotelInvolved === true || u.isExternalHotelInvolved === 1,
        isPandalInvolved: u.isPandalInvolved === true || u.isPandalInvolved === 1,
        statusReason: u.statusReason || '',
        comments: u.comments || '',
        photoUrl: u.photoUrl || '',
      });
    }
  }

  // when a festival is picked, suggest its name as the title (only if empty)
  onMasterChange(): void {
    const picked = this.masters.find((m) => String(m.utsavId) === String(this.form.value.utsavId));
    if (picked && !this.form.value.utsavTitle) {
      this.form.patchValue({ utsavTitle: picked.name });
    }
  }

  // pick the photo from the central media library
  pickImage(): void {
    const ref = this.dialog.open(MediaPickerDialogComponent, {
      width: '760px',
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: 'calc(100dvh - 32px)',
      data: { establishmentId: null, category: 'UTSAV', selectedUrl: this.form.get('photoUrl')?.value || '' },
    });
    ref.afterClosed().subscribe((url: string | undefined) => {
      if (url) this.form.patchValue({ photoUrl: url });
    });
  }

  clearImage(): void {
    this.form.patchValue({ photoUrl: '' });
  }

  submit(): void {
    if (this.form.invalid) {
      this.toastService.error(
        'Fill every required field: title, abbreviation, year, location, and all utsav / booking / accommodation dates.'
      );
      return;
    }
    this.isLoading = true;
    const request$ = this.isEdit
      ? this.operatorAdminService.updateUtsav(this.data.utsav.utsavInstanceId, this.form.value)
      : this.operatorAdminService.addUtsav(this.form.value);

    request$.subscribe({
      next: (res: any) => {
        this.toastService.success(res?.message || 'Utsav saved.');
        this.isLoading = false;
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to save utsav.');
      },
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
